import api from "../../../api/axios";
import {
  createGeoSearchTimeoutController,
  isGeoSearchTimeoutError,
  toGeoSearchTimeoutError,
} from "../lib/geoSearchTimeout";

// Feature-local Geo API client (RIC-5.5). Read-only KATOTTG endpoints.

const GEO_BASE = "/geo";

export const MIN_SETTLEMENT_QUERY_LENGTH = 2;
export const MIN_ADDRESS_OBJECT_QUERY_LENGTH = MIN_SETTLEMENT_QUERY_LENGTH;

function withGeoSearchTimeout(signal, request) {
  const { signal: timeoutSignal, cleanup } = createGeoSearchTimeoutController(
    signal,
  );
  return request(timeoutSignal)
    .then((data) => {
      cleanup();
      return data;
    })
    .catch((err) => {
      cleanup();
      if (isGeoSearchTimeoutError(err, timeoutSignal)) {
        return Promise.reject(toGeoSearchTimeoutError());
      }
      return Promise.reject(err);
    });
}

export const getGeoRegions = () =>
  api.get(`${GEO_BASE}/regions/`).then((res) => res.data);

export const searchGeoSettlements = ({ query, regionCode, page = 1, signal }) => {
  const trimmed = String(query ?? "").trim();
  if (trimmed.length < MIN_SETTLEMENT_QUERY_LENGTH) {
    return Promise.reject(new Error("Settlement query too short"));
  }
  const params = { q: trimmed, page };
  if (regionCode) {
    params.region_code = regionCode;
  }
  return withGeoSearchTimeout(signal, (timeoutSignal) =>
    api
      .get(`${GEO_BASE}/settlements/`, { params, signal: timeoutSignal })
      .then((res) => res.data),
  );
};

export const searchGeoAddressObjects = ({
  settlementCode,
  query,
  kind,
  page = 1,
  signal,
}) => {
  if (!settlementCode) {
    return Promise.reject(new Error("Address object search requires a settlement"));
  }
  const trimmed = String(query ?? "").trim();
  if (trimmed.length < MIN_ADDRESS_OBJECT_QUERY_LENGTH) {
    return Promise.reject(new Error("Address object query too short"));
  }
  const params = {
    settlement_code: settlementCode,
    q: trimmed,
    page,
  };
  if (kind) {
    params.kind = kind;
  }
  return withGeoSearchTimeout(signal, (timeoutSignal) =>
    api
      .get(`${GEO_BASE}/address-objects/`, { params, signal: timeoutSignal })
      .then((res) => res.data),
  );
};

export const getGeoAddressObject = ({ settlementCode, id, signal } = {}) => {
  if (!settlementCode) {
    return Promise.reject(new Error("Address object lookup requires a settlement"));
  }
  if (id == null || id === "") {
    return Promise.reject(new Error("Address object lookup requires an id"));
  }
  return api
    .get(`${GEO_BASE}/address-objects/`, {
      params: { settlement_code: settlementCode, id },
      signal,
    })
    .then((res) => {
      const results = res.data?.results;
      if (Array.isArray(results) && results.length) return results[0];
      return null;
    });
};

export const getGeoUnit = (code, { signal } = {}) =>
  api
    .get(`${GEO_BASE}/units/${encodeURIComponent(code)}/`, { signal })
    .then((res) => res.data);

/** Authenticated short-link / map-URL coordinate resolver. */
export const resolveMapLinkCoordinates = (url, { signal } = {}) =>
  api
    .post(`${GEO_BASE}/map-link/coordinates/`, { url }, { signal })
    .then((res) => res.data);
